import { create } from "zustand";
import { persist } from "zustand/middleware";
import { WalletController } from "@/api/WalletController";
import { ISnipeItem } from "@/api/IWalletController";

import { IAsset } from "@/api/IWalletController";
import { useAssets } from "@/stores/assets.store";

interface ISnipeWithAssetInfo extends ISnipeItem {
    assetInInfo?: IAsset;
    assetOutInfo?: IAsset;
    revealed?: boolean;
}

interface ISnipesState {
    snipes: ISnipeWithAssetInfo[];
    isLoading: boolean;
    error: string | null;
    fetchSnipes: () => Promise<void>;
    cancelSnipe: (snipeId: string) => Promise<void>;
    updateSnipe: (payload: any) => Promise<any>;
}

export const useSnipesStore = create<ISnipesState>()(
    persist(
        (set, get) => ({
            snipes: [],
            isLoading: false,
            error: null,

            fetchSnipes: async () => {
                set((state) => (state.snipes.length === 0 ? { isLoading: true, error: null } : { error: null }));
                try {
                    const response = await WalletController.getSniperList();
                    if (response.status === "success" && response.data) {
                        const sortedSnipes = response.data.sort(
                            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                        );
                        // Gather all unique assetInGuid and assetOutGuid
                        const uniqueGuids = Array.from(
                            new Set([
                                ...sortedSnipes.map((s) => s.assetInGuid),
                                ...sortedSnipes.map((s) => s.assetOutGuid),
                            ]),
                        );
                        useAssets
                            .getState()
                            .findAssets(uniqueGuids.map((guid) => ({ guid })))
                            .then((assets: IAsset[]) => {
                                // Map guid to asset
                                const guidToAsset: Record<string, IAsset> = {};
                                assets.forEach((asset) => {
                                    guidToAsset[asset.guid] = asset;
                                });
                                // Attach assetInInfo, assetOutInfo, and revealed to each snipe
                                const prevSnipes = get().snipes || [];
                                const prevGuids = new Set(prevSnipes.map((s) => s.guid));
                                const snipesWithAssetInfo = sortedSnipes.map((snipe) => ({
                                    ...snipe,
                                    assetInInfo: guidToAsset[snipe.assetInGuid] || undefined,
                                    assetOutInfo: guidToAsset[snipe.assetOutGuid] || undefined,
                                    revealed: prevGuids.has(snipe.guid),
                                }));
                                set({ snipes: snipesWithAssetInfo, isLoading: false });
                                // After a short delay, reveal new snipes
                                setTimeout(() => {
                                    set((state) => ({
                                        snipes: state.snipes.map((snipe) =>
                                            !prevGuids.has(snipe.guid) ? { ...snipe, revealed: true } : snipe,
                                        ),
                                    }));
                                }, 50);
                            })
                            .catch(() => {
                                // Fallback: set snipes without asset info
                                set({ snipes: sortedSnipes, isLoading: false });
                            });
                    } else {
                        throw new Error(response.message || "Failed to fetch snipes");
                    }
                } catch (err) {
                    const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
                    console.error("Error fetching snipes:", err);
                    set({ error: errorMessage, isLoading: false });
                }
            },

            cancelSnipe: async (snipeId: string) => {
                try {
                    await WalletController.cancelSniper({ snipeId });
                    // Gently update the cancelled snipe's status instead of removing it
                    set((state) => ({
                        snipes: state.snipes.map(
                            (snipe) => (snipe.guid === snipeId ? { ...snipe, status: 1 } : snipe), // 1 = Cancelled
                        ),
                    }));
                } catch (err) {
                    const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
                    set({ error: errorMessage });
                    console.error("Error canceling snipe:", err);
                }
            },

            /**
             * Update snipes by sending an array of snipe IDs.
             * Updates all matching snipes in the store with returned data.
             * @param snipeIds - Array of snipe IDs to update
             */
            updateSnipe: async (snipeIds: string[]) => {
                try {
                    const response = await WalletController.updateSnipe(snipeIds);
                    // Assume response.data is an array of updated snipes, each with a guid
                    set((state) => {
                        if (!Array.isArray(response.data)) return {};
                        const updatedSnipesMap = Object.fromEntries(
                            response.data.map((snipe: any) => [snipe.guid, snipe]),
                        );
                        return {
                            snipes: state.snipes.map((snipe) =>
                                updatedSnipesMap[snipe.guid] ? { ...snipe, ...updatedSnipesMap[snipe.guid] } : snipe,
                            ),
                        };
                    });
                } catch (err) {
                    const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
                    set({ error: errorMessage });
                    console.error("Error updating snipes:", err);
                }
            },
        }),
        {
            name: "snipes-storage",
        },
    ),
);
